import React, { useCallback, useRef, useState, useEffect } from "react";
import {
  AppProvider,
  ActionList,
  Card,
  TextField,
  TextContainer,
  ContextualSaveBar,
  FormLayout,
  Modal,
  Frame,
  Layout,
  Loading,
  Navigation,
  Page,
  SkeletonBodyText,
  SkeletonDisplayText,
  SkeletonPage,
  Toast,
  TopBar,
  Text,
} from "@shopify/polaris";
import {
  ViewMajor,
  SettingsMajor,
  LanguageMinor,
  ProfileMajor,
  IconsMajor,
  AnalyticsMajor,
  NotificationMajor,
  LockMajor,
  BlogMajor,
  LinkMinor,
  DnsSettingsMajor,
  NavigationMajor,
  ChannelsMajor,
  CategoriesMajor,
  JobsMajor,
  FileFilledMinor,
  ProfileIcon,
  ExitIcon,
  ChartVerticalIcon,
  PageIcon,
  LanguageIcon,
  MenuIcon,
  TeamIcon,
  CategoriesIcon,
  PaymentFilledIcon,
  AutomationIcon,
  BlogIcon,
  ViewIcon,
  ThemeTemplateIcon,
  SettingsIcon,
  ArrowUpIcon,
  WorkIcon,
  FileIcon,
  ChannelsIcon,
  LinkIcon,
  IconsIcon,
  NotificationIcon,
  LockIcon,
  DnsSettingsIcon,
  TextIcon,
  PaymentIcon,
  CalendarIcon,
  SearchListIcon,
  ClipboardChecklistIcon,
} from "@shopify/polaris-icons";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import {
  adminRole,
  accessDashboard,
  accessViewPages,
  accessUiText,
  accessNavigation,
  accessCrm,
  accessViewBlogs,
  accessSettings,
} from "../Helpers";
import axios from "../Assets/Lib/axios";
import bookmarkPin from "../Assets/Images/bookmark-pin.svg";

function NavigationLayout(props) {
  const navigate = useNavigate();
  const client = process.env.REACT_APP_CLIENT;
  const logoImg = `/logos/${client}-logoWhite.svg`;
  const defaultState = useRef({
    emailFieldValue: "dharma@jadedpixel.com",
    nameFieldValue: "Jaded Pixel",
  });
  const skipToContentRef = useRef(null);
  const [toastActive, setToastActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [searchActive, setSearchActive] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [userMenuActive, setUserMenuActive] = useState(false);
  const [mobileNavigationActive, setMobileNavigationActive] = useState(false);
  const [modalActive, setModalActive] = useState(false);
  const [nameFieldValue, setNameFieldValue] = useState(
    defaultState.current.nameFieldValue
  );
  const [emailFieldValue, setEmailFieldValue] = useState(
    defaultState.current.emailFieldValue
  );
  const [storeName, setStoreName] = useState(
    defaultState.current.nameFieldValue
  );
  const [supportSubject, setSupportSubject] = useState("");
  const [supportMessage, setSupportMessage] = useState("");

  const handleSubjectChange = useCallback(
    (value) => setSupportSubject(value),
    []
  );
  const handleMessageChange = useCallback(
    (value) => setSupportMessage(value),
    []
  );
  const handleDiscard = useCallback(() => {
    setEmailFieldValue(defaultState.current.emailFieldValue);
    setNameFieldValue(defaultState.current.nameFieldValue);
    setIsDirty(false);
  }, []);
  const handleSave = useCallback(() => {
    defaultState.current.nameFieldValue = nameFieldValue;
    defaultState.current.emailFieldValue = emailFieldValue;

    setIsDirty(false);
    setToastActive(true);
    setStoreName(defaultState.current.nameFieldValue);
  }, [emailFieldValue, nameFieldValue]);
  const handleNameFieldChange = useCallback((value) => {
    setNameFieldValue(value);
    value && setIsDirty(true);
  }, []);
  const handleEmailFieldChange = useCallback((value) => {
    setEmailFieldValue(value);
    value && setIsDirty(true);
  }, []);
  const handleSearchResultsDismiss = useCallback(() => {
    setSearchActive(false);
    setSearchValue("");
  }, []);
  const handleSearchFieldChange = useCallback((value) => {
    setSearchValue(value);
    setSearchActive(value.length > 0);
  }, []);
  const toggleToastActive = useCallback(
    () => setToastActive((toastActive) => !toastActive),
    []
  );
  const toggleUserMenuActive = useCallback(
    () => setUserMenuActive((userMenuActive) => !userMenuActive),
    []
  );
  const toggleMobileNavigationActive = useCallback(
    () =>
      setMobileNavigationActive(
        (mobileNavigationActive) => !mobileNavigationActive
      ),
    []
  );
  const toggleIsLoading = useCallback(
    () => setIsLoading((isLoading) => !isLoading),
    []
  );
  const toggleModalActive = useCallback(
    () => setModalActive((modalActive) => !modalActive),
    []
  );

  const toastMarkup = toastActive ? (
    <Toast onDismiss={toggleToastActive} content="Changes saved" />
  ) : null;

  const userMenuActions = [
    {
      items: [
        {
          content: "Your profile",
          icon: ProfileIcon,
          onAction: () => navigate("/profile"),
        },
      ],
    },
    {
      items: [
        {
          content: "Log out",
          icon: ExitIcon,

          onAction: () => {
            axios
              .post(`/logout`)
              .then((result) => {
                Cookies.set("cms-accesstoken", null);
                navigate("/");
              })
              .catch((err) => console.log(err));
          },
        },
      ],
    },
  ];
  const customIcon = {
    viewBox: "0 0 20 20",
    body: '<g transform="scale(0.5)"><path d="M16 0 C6 0 2 4 2 14 L2 22 L6 24 L6 30 L26 30 L26 24 L30 22 L30 14 C30 4 26 0 16 0 M9 12 A4.5 4.5 0 0 1 9 21 A4.5 4.5 0 0 1 9 12 M23 12 A4.5 4.5 0 0 1 23 21 A4.5 4.5 0 0 1 23 12"></path></g>',
  };
  const contextualSaveBarMarkup = isDirty ? (
    <ContextualSaveBar
      message="Unsaved changes"
      saveAction={{
        onAction: handleSave,
      }}
      discardAction={{
        onAction: handleDiscard,
      }}
    />
  ) : null;

  const userMenuMarkup = (
    <TopBar.UserMenu
      actions={userMenuActions}
      name={Cookies.get("cms-name") && Cookies.get("cms-name")}
      initials={
        Cookies.get("cms-name") &&
        Cookies.get("cms-name").split(" ")[0].charAt(0) +
          " " +
          Cookies.get("cms-name").split(" ")[1].charAt(0)
      }
      open={userMenuActive}
      onToggle={toggleUserMenuActive}
    />
  );
  // const secondaryMenuMarkup = (
  //   <TopBar.Menu
  //     activatorContent={[<span>aaa</span>]}
  //     // activatorContent={
  //     //   <div className="rowDiv">
  //     //     <div>a</div>
  //     //     <div className="ml-5" style={{ marginRight: "2rem" }}>
  //     //       b
  //     //     </div>
  //     //     <div>c</div>
  //     //   </div>
  //     // }
  //     // activatorContent={
  //     //   <div className="rowDiv">
  //     //     <div>a</div>
  //     //     <div className="ml-5" style={{ marginRight: "2rem" }}>
  //     //       b
  //     //     </div>
  //     //     <div>c</div>
  //     //   </div>
  //     // }
  //     open={true}
  //     // onOpen={toggleIsSecondaryMenuOpen}
  //     // onClose={toggleIsSecondaryMenuOpen}
  //     // actions={[
  //     //   {
  //     //     items: [{ content: "Community forums" }],
  //     //   },
  //     // ]}
  //   />
  // );
  const [bookmarks, setBookmarks] = useState(null);
  useEffect(() => {
    axios
      .get(`/bookmarks`)
      .then((result) => {
        setBookmarks(result.data.data);
      })
      .catch((err) => console.log(err));
  }, []);

  const dashboardTab = {
    url: "/",
    onClick: () => {
      navigate("/admin/dashboard");
      const newState = ref.current.map((obj) => {
        if (obj.label === "Dashboard") {
          return { ...obj, selected: true };
        }
        return { ...obj, selected: false };
      });
      setMenuTabs(newState);
    },
    label: "Dashboard",
    icon: ChartVerticalIcon,
    selected: window.location.href.indexOf("/admin/dashboard") > -1,
    disabled: !accessDashboard(),
    // exactMatch: true,
  };
  const pagesTab = {
    url: "/",
    onClick: () => {
      navigate("/admin/pages");
      const newState = ref.current.map((obj) => {
        if (obj.label === "Pages") {
          return { ...obj, selected: true };
        }
        return { ...obj, selected: false };
      });
      setMenuTabs(newState);
    },
    label: "Pages",
    icon: PageIcon,
    selected: window.location.href.indexOf("/admin/pages") > -1,
    disabled: !accessViewPages(),
  };
  const uiTab = {
    url: "/",
    onClick: () => {
      navigate("/admin/ui-text");
      const newState = ref.current.map((obj) => {
        if (obj.label === "UI Text") {
          return { ...obj, selected: true };
        }
        return { ...obj, selected: false };
      });
      setMenuTabs(newState);
    },
    label: "UI Text",
    icon: LanguageIcon,
    selected: false,
    exactMatch: window.location.href.indexOf("/admin/ui-tab") > -1,
    disabled: !accessUiText(),
  };
  const navigationTab = {
    url: "/",
    onClick: () => {
      navigate("/admin/navigation");
      const newState = ref.current.map((obj) => {
        if (obj.label === "Navigation") {
          return { ...obj, selected: true };
        }
        return { ...obj, selected: false };
      });
      setMenuTabs(newState);
    },
    label: "Navigation",
    icon: MenuIcon,
    selected: false,
    exactMatch: window.location.href.indexOf("/admin/navigation") > -1,
    disabled: !accessNavigation(),
  };
  const crmTab = {
    disabled: !accessCrm(),
    label: "CRM",
    url: "/",
    onClick: () => {
      navigate("/admin/crm/contacts");
      console.log(ref.current);
      const newState = ref.current.map((obj) => {
        if (obj.label === "CRM") {
          return {
            ...obj,
            selected: true,
            subNavigationItems: obj.subNavigationItems.map((obj2) => {
              if (obj2.label === "Contacts") {
                return {
                  ...obj2,
                  selected: true,
                };
              }
              return { ...obj2, selected: false };
            }),
          };
        }
        return { ...obj, selected: false };
      });
      setMenuTabs(newState);
    },
    icon: TeamIcon,
    selected: window.location.href.indexOf("/admin/crm") > -1,
    subNavigationItems: [
      {
        onClick: () => {
          navigate("/admin/crm/contacts");
          const newState = ref.current.map((obj) => {
            if (obj.label === "CRM") {
              return {
                ...obj,
                selected: true,
                subNavigationItems: obj.subNavigationItems.map((obj2) => {
                  if (obj2.label === "Contacts") {
                    return {
                      ...obj2,
                      selected: true,
                    };
                  }
                  return { ...obj2, selected: false };
                }),
              };
            }
            return { ...obj, selected: false };
          });
          setMenuTabs(newState);
        },

        selected: window.location.href.indexOf("/admin/crm/contacts") > -1,
        label: "Contacts",
        icon: TeamIcon,
      },
      // {
      //   onClick: () => {
      //     navigate("/admin/crm/companies");
      //     const newState = ref.current.map((obj) => {
      //       if (obj.label === "CRM") {
      //         return {
      //           ...obj,
      //           selected: true,
      //           subNavigationItems: obj.subNavigationItems.map((obj2) => {
      //             if (obj2.label === "Companies") {
      //               return {
      //                 ...obj2,
      //                 selected: true,
      //               };
      //             }
      //             return { ...obj2, selected: false };
      //           }),
      //         };
      //       }
      //       return { ...obj, selected: false };
      //     });
      //     setMenuTabs(newState);
      //   },

      //   selected: window.location.href.indexOf("/admin/crm/companies") > -1,
      //   label: "Companies",
      //   icon: CategoriesMajor,
      // },
      {
        onClick: () => {
          navigate("/admin/crm/campaigns");
          const newState = ref.current.map((obj) => {
            if (obj.label === "CRM") {
              return {
                ...obj,
                selected: true,
                subNavigationItems: obj.subNavigationItems.map((obj2) => {
                  if (obj2.label === "Campaigns") {
                    return {
                      ...obj2,
                      selected: true,
                    };
                  }
                  return { ...obj2, selected: false };
                }),
              };
            }
            return { ...obj, selected: false };
          });
          setMenuTabs(newState);
        },

        selected: window.location.href.indexOf("/admin/crm/campaigns") > -1,
        label: "Campaigns",
        icon: CategoriesIcon,
      },
      // {
      //   onClick: () => {
      //     navigate("/admin/crm/contacts-board");
      //     console.log(ref.current);
      //     const newState = ref.current.map((obj) => {
      //       if (obj.label === "CRM") {
      //         return {
      //           ...obj,
      //           selected: true,
      //           subNavigationItems: obj.subNavigationItems.map((obj2) => {
      //             if (obj2.label === "Contacts Board") {
      //               return {
      //                 ...obj2,
      //                 selected: true,
      //               };
      //             }
      //             return { ...obj2, selected: false };
      //           }),
      //         };
      //       }
      //       return { ...obj, selected: false };
      //     });
      //     setMenuTabs(newState);
      //   },

      //   selected:
      //     window.location.href.indexOf("/admin/crm/contacts-board") > -1,
      //   label: "Contacts Board",
      //   icon: Columns3Major,
      // },
      {
        onClick: () => {
          navigate("/admin/crm/deals");
          console.log(ref.current);
          const newState = ref.current.map((obj) => {
            if (obj.label === "CRM") {
              return {
                ...obj,
                selected: true,
                subNavigationItems: obj.subNavigationItems.map((obj2) => {
                  if (obj2.label === "Deals") {
                    return {
                      ...obj2,
                      selected: true,
                    };
                  }
                  return { ...obj2, selected: false };
                }),
              };
            }
            return { ...obj, selected: false };
          });
          setMenuTabs(newState);
        },

        selected: window.location.href.indexOf("/admin/crm/deals") > -1,
        label: "Deals",
        icon: PaymentFilledIcon,
      },

      {
        onClick: () => {
          navigate("/admin/crm/automation");
          console.log(ref.current);
          const newState = ref.current.map((obj) => {
            if (obj.label === "CRM") {
              return {
                ...obj,
                selected: true,
                subNavigationItems: obj.subNavigationItems.map((obj2) => {
                  if (obj2.label === "Automation") {
                    return {
                      ...obj2,
                      selected: true,
                    };
                  }
                  return { ...obj2, selected: false };
                }),
              };
            }
            return { ...obj, selected: false };
          });
          setMenuTabs(newState);
        },

        selected: window.location.href.indexOf("/admin/crm/automation") > -1,
        label: "Automation",
        icon: AutomationIcon,
      },
    ],
  };
  const blogsTab = {
    disabled: accessViewBlogs() === false,
    url: "/",
    onClick: () => {
      navigate("/admin/blogs");
      const newState = ref.current.map((obj) => {
        if (obj.label === "Blogs") {
          return { ...obj, selected: true };
        }
        return { ...obj, selected: false };
      });
      setMenuTabs(newState);
    },
    label: "Blogs",
    icon: BlogIcon,
    selected: window.location.href.indexOf("/admin/blogs") > -1,
  };
  const ref = useRef([
    // dashboardTab,
    // pagesTab,
    // blogsTab,
    // uiTab,
    // navigationTab,
    // crmTab,
  ]);
  const [menuTabs, setMenuTabs] = useState([
    // dashboardTab,
    // pagesTab,
    // blogsTab,
    // uiTab,
    // navigationTab,
    // crmTab,
  ]);
  const [openParent, setOpenParent] = useState(null); // track open parent tab
  const [displayWebsiteContent, setDisplayWebsiteContent] = useState({});
  const [displayCrmContent, setDisplayCrmContent] = useState({});

  useEffect(() => {
    axios
      .get(`/settings/display-content`)
      .then((result) => {
        setDisplayWebsiteContent(result.data.data[2].website);
        setDisplayCrmContent(result.data.data[0].crm);
      })
      .catch((err) => console.log(err));
  }, []);

  const isVisible = (key) => displayWebsiteContent[key] === "1";
  const isVisibleCrm = (key) => displayCrmContent[key] === "1";
  const websiteSubItems = [
    isVisible("dashboard") && {
      onClick: () => navigate("/admin/dashboard"),
      selected: window.location.href.includes("/admin/dashboard"),
      label: "Dashboard",
      icon: ChartVerticalIcon,
    },
    isVisible("pages") && {
      onClick: () => navigate("/admin/pages"),
      selected: window.location.href.includes("/admin/pages"),
      label: "Pages",
      icon: PageIcon,
    },
    isVisible("blogs") && {
      onClick: () => navigate("/admin/blogs"),
      selected: window.location.href.includes("/admin/blogs"),
      label: "Blogs",
      icon: BlogIcon,
    },
    isVisible("ui_text") && {
      onClick: () => navigate("/admin/ui-text"),
      selected: window.location.href.includes("/admin/ui-text"),
      label: "UI Text",
      icon: TextIcon,
    },
    isVisible("navigation") && {
      onClick: () => navigate("/admin/navigation"),
      selected: window.location.href.includes("/admin/navigation"),
      label: "Navigation",
      icon: MenuIcon,
    },
  ].filter(Boolean); // remove false entries

  const crmSubItems = [
    isVisibleCrm("contacts") && {
      onClick: () => navigate("/admin/crm/contacts"),
      selected: window.location.href.includes("/admin/crm/contacts"),
      label: "Contacts",
      icon: TeamIcon,
    },
    isVisibleCrm("campaigns") && {
      onClick: () => navigate("/admin/crm/campaigns"),
      selected: window.location.href.includes("/admin/crm/campaigns"),
      label: "Campaigns",
      icon: CategoriesIcon,
    },
    isVisibleCrm("deals") && {
      onClick: () => navigate("/admin/crm/deals"),
      selected: window.location.href.includes("/admin/crm/deals"),
      label: "Deals",
      icon: PaymentIcon,
    },
    isVisibleCrm("automation") && {
      onClick: () => navigate("/admin/crm/automation"),
      selected: window.location.href.includes("/admin/crm/automation"),
      label: "Automation",
      icon: AutomationIcon,
    },
  ].filter(Boolean); // remove false entries
  const secondaryMenuMarkup = (
    // <TopBar.Menu
    //   activatorContent={
    <div className="rowDiv">
      {bookmarks &&
        bookmarks.map((element, index) => {
          return (
            <div className="mytooltip rowDiv" key={index}>
              <div
                key={element.url}
                className="bookmarkDiv me-3 tooltiptext "
                style={{
                  backgroundColor: element.color ? element.color : "#efefef",
                }}
                // className="me-3"
                onClick={() => {
                  window.open(element.url, "_blank");
                }}
              >
                <img
                  src={bookmarkPin}
                  width={23}
                  className="centered-element"
                />
                <p style={{ marginLeft: "2rem" }}>{element.name}</p>
              </div>
              <span className="mytext">{element.url}</span>
            </div>

            // <div className="tooltipDiv rowDiv" key={index}>
            //   <span
            //     key={index}
            //     className="bookmarkDiv me-3 tooltiptext"
            //     style={{ cursor: "pointer", color: "black" }}
            //     // className="me-3"
            //     onClick={() => {
            //       window.open(element.url, "_blank");
            //     }}
            //   >
            //     {element.name}
            //   </span>
            //   <span className="tooltiptext">Tooltip text</span>
            // </div>

            // <span
            //   key={index}
            //   className="bookmarkDiv me-3"
            //   style={{ cursor: "pointer", color: "black" }}
            //   // className="me-3"
            //   onClick={() => {
            //     window.open(element.url, "_blank");
            //   }}
            // >
            //   {element.name}
            // </span>
          );
        })}
    </div>
  );
  const searchResultsMarkup = (
    <ActionList
      items={[
        { content: "Shopify help center" },
        { content: "Community forums" },
      ]}
    />
  );

  const searchFieldMarkup = (
    <TopBar.SearchField
      onChange={handleSearchFieldChange}
      value={searchValue}
      placeholder="Search"
    />
  );

  const topBarMarkup = (
    <TopBar
      showNavigationToggle
      userMenu={userMenuMarkup}
      secondaryMenu={secondaryMenuMarkup}
      searchResults={searchResultsMarkup}
      searchResultsVisible={true}
      // searchField={searchFieldMarkup}
      // searchResults={searchResultsMarkup}
      // onSearchResultsDismiss={handleSearchResultsDismiss}
      onNavigationToggle={toggleMobileNavigationActive}
      logoSuffix
    />
  );

  const hideCompetitors = false;
  const hidePersonas = false;
  const navigationItems = [
    websiteSubItems.length > 0 && {
      url: "/",
      label: "Website",
      onClick: () => {
        const newState = ref.current.map((obj) => ({
          ...obj,
          selected: false,
        }));
        setMenuTabs(newState);
        navigate("/admin/pages");
      },
      icon: LinkIcon,
      selected:
        window.location.href.includes("/admin/dashboard") ||
        window.location.href.includes("/admin/pages") ||
        window.location.href.includes("/admin/blogs") ||
        window.location.href.includes("/admin/ui-text") ||
        window.location.href.includes("/admin/navigation"),
      subNavigationItems: websiteSubItems,
    },

    crmSubItems.length > 0 && {
      url: "/",
      label: "CRM",
      onClick: () => {
        const newState = ref.current.map((obj) => ({
          ...obj,
          selected: false,
        }));
        setMenuTabs(newState);
        navigate("/admin/crm/contacts");
      },
      icon: TeamIcon,
      selected:
        window.location.href.includes("/admin/crm/contacts") ||
        window.location.href.includes("/admin/crm/campaigns") ||
        window.location.href.includes("/admin/crm/deals") ||
        window.location.href.includes("/admin/crm/automation"),
      subNavigationItems: crmSubItems,
    },

    {
      url: "/",
      label: "Growth",
      onClick: () => {
        const newState = ref.current.map((obj) => ({
          ...obj,
          selected: false,
        }));
        setMenuTabs(newState);
        navigate("/admin/competitors");
      },
      icon: ArrowUpIcon,
      selected:
        window.location.href.includes("/admin/competitors") ||
        window.location.href.includes("/admin/personas") ||
        window.location.href.includes("/admin/goals") ||
        window.location.href.includes("/admin/achievements") ||
        window.location.href.includes("/admin/benchmarks") ||
        window.location.href.includes("/admin/people") ||
        window.location.href.includes("/admin/brand-benchmark") ||
        window.location.href.includes("/admin/projects") ||
        window.location.href.includes("/admin/team") ||
        window.location.href.includes("/admin/task-manager"),
      subNavigationItems: [
        {
          onClick: () => navigate("/admin/competitors"),
          selected:
            window.location.href.includes("/admin/competitors") ||
            window.location.href.includes("/admin/brand-benchmark"),
          label: "Competitors",
          icon: TeamIcon,
        },
        {
          onClick: () => navigate("/admin/personas"),
          selected: window.location.href.includes("/admin/personas"),
          label: "Personas",
          icon: ProfileIcon,
        },
        {
          onClick: () => navigate("/admin/goals"),
          selected: window.location.href.includes("/admin/goals"),
          label: "Goals",
          icon: () => (
            <img
              src="/icons/goals.svg"
              alt="Custom Icon"
              width="200"
              height="200"
              style={{ display: "block" }}
            />
          ),
        },
        {
          onClick: () => navigate("/admin/achievements"),
          selected: window.location.href.includes("/admin/achievements"),
          label: "Achievements",
          icon: CategoriesIcon,
        },
        {
          onClick: () => navigate("/admin/benchmarks"),
          selected: window.location.href.includes("/admin/benchmarks"),
          label: "Benchmarks",
          icon: SearchListIcon,
        },
        {
          onClick: () => navigate("/admin/people"),
          selected: window.location.href.includes("/admin/people"),
          label: "People",
          icon: TeamIcon,
        },
        {
          onClick: () => navigate("/admin/projects"),
          selected:
            window.location.href.includes("/admin/projects") ||
            window.location.href.includes("/admin/team"),
          label: "Projects",
          icon: CategoriesIcon,
        },
        {
          onClick: () => {
            Object.keys(sessionStorage).forEach((key) => {
              if (
                key.startsWith("task_selected_index_") ||
                key.startsWith("task_status_")
              ) {
                sessionStorage.removeItem(key);
              }
            });
            navigate("/admin/task-manager/projects");
          },
          selected:
            window.location.href.includes("/admin/task-manager/projects") ||
            window.location.href.includes("/admin/task-manager"),
          label: "Task Manager",
          icon: ClipboardChecklistIcon,
        },
      ],
    },
  ].filter(Boolean); // remove any false/null entries

  const navigationMarkup = (
    <Navigation location="/">
      <Navigation.Section items={menuTabs.concat(navigationItems)} />
      <Navigation.Section
        separator
        items={[
          {
            label: "Settings",
            disabled: !accessSettings(),
            url: "/",
            onClick: () => {
              navigate("/admin/settings/languages");
              const newState = ref.current.map((obj) => ({
                ...obj,
                selected: false,
              }));
              setMenuTabs(newState);
            },
            icon: SettingsIcon,
            selected: window.location.href.indexOf("/admin/settings") > -1,
            subNavigationItems: [
              {
                onClick: () => navigate("/admin/settings/languages"),
                selected:
                  window.location.href.indexOf("/admin/settings/languages") >
                  -1,
                label: "Languages",
                icon: LanguageIcon,
              },
              {
                onClick: () => navigate("/admin/settings/channels"),
                selected:
                  window.location.href.indexOf("/admin/settings/channels") > -1,
                label: "Channels",
                icon: ChannelsIcon,
              },
              {
                onClick: () =>
                  navigate("/admin/settings/social-media-and-links"),
                selected:
                  window.location.href.indexOf("/admin/settings/social-media") >
                  -1,
                label: "Social Media and Links",
                icon: LinkIcon,
              },
              {
                disabled: adminRole() === "Editor",
                onClick: () => navigate("/admin/settings/brand"),
                selected:
                  window.location.href.indexOf("/admin/settings/brand") > -1,
                label: "Brand",
                icon: IconsIcon,
              },
              {
                onClick: () => navigate("/admin/settings/contact-information"),
                selected:
                  window.location.href.indexOf(
                    "/admin/settings/contact-information"
                  ) > -1,
                label: "Notifications",
                icon: NotificationIcon,
                disabled: adminRole() === "Editor",
              },
              {
                onClick: () =>
                  navigate("/admin/settings/users-and-permissions"),
                selected:
                  window.location.href.indexOf(
                    "/admin/settings/users-and-permissions"
                  ) > -1,
                label: "Users and Permissions",
                icon: ProfileIcon,
                disabled: adminRole() !== "Super",
              },
              {
                onClick: () => navigate("/admin/settings/bookmarks"),
                selected:
                  window.location.href.indexOf("/admin/settings/bookmarks") >
                  -1,
                label: "Bookmarks",
                icon: LinkIcon,
              },
              {
                onClick: () => navigate("/admin/settings/access-password"),
                selected:
                  window.location.href.indexOf(
                    "/admin/settings/access-password"
                  ) > -1,
                label: "Access Password",
                icon: LockIcon,
                disabled: adminRole() === "Editor",
              },
              {
                onClick: () => navigate("/admin/settings/integrations"),
                selected:
                  window.location.href.indexOf("/admin/settings/integrations") >
                  -1,
                label: "Integrations",
                icon: DnsSettingsIcon,
              },
              {
                onClick: () =>
                  navigate("/admin/settings/sections-customization"),
                selected:
                  window.location.href.indexOf(
                    "/admin/settings/sections-customization"
                  ) > -1,
                label: "Display/Hide Sections",
                icon: ViewIcon,
              },
              {
                onClick: () => navigate("/admin/settings/templates"),
                selected:
                  window.location.href.indexOf("/admin/settings/templates") >
                  -1,
                label: "Templates",
                icon: ThemeTemplateIcon,
              },
              {
                onClick: () => navigate("/admin/settings/app-settings"),
                selected:
                  window.location.href.indexOf("/admin/settings/app-settings") >
                  -1,
                label: "App Settings",
                icon: SettingsIcon,
              },
              (process.env.REACT_APP_CLIENT === "innovest" ||
                process.env.REACT_APP_CLIENT === "face" ||
                process.env.REACT_APP_CLIENT === "chapha") && {
                onClick: () => navigate("/admin/settings/deploy"),
                selected:
                  window.location.href.indexOf("/admin/settings/deploy") > -1,
                label: "Publish",
                icon: ArrowUpIcon,
              },
              {
                onClick: () => navigate("/admin/settings/countries"),
                selected:
                  window.location.href.indexOf("/admin/settings/countries") >
                  -1,
                label: "Countries",
                icon: CategoriesIcon,
              },
            ].filter(Boolean), // remove false/null entries
          },
        ]}
      />
    </Navigation>
  );

  const loadingMarkup = isLoading ? <Loading /> : null;

  const skipToContentTarget = (
    <a id="SkipToContentTarget" ref={skipToContentRef} tabIndex={-1} />
  );

  const actualPageMarkup = (
    <Page title="Account">
      <Layout>
        {skipToContentTarget}
        <Layout.AnnotatedSection
          title="Account details"
          description="Jaded Pixel will use this as your account information."
        >
          <Card sectioned>
            <FormLayout>
              <TextField
                label="Full name"
                value={nameFieldValue}
                onChange={handleNameFieldChange}
              />
              <TextField
                type="email"
                label="Email"
                value={emailFieldValue}
                onChange={handleEmailFieldChange}
              />
            </FormLayout>
          </Card>
        </Layout.AnnotatedSection>
      </Layout>
    </Page>
  );

  const loadingPageMarkup = (
    <SkeletonPage>
      <Layout>
        <Layout.Section>
          <Card sectioned>
            <TextContainer>
              <SkeletonDisplayText size="small" />
              <SkeletonBodyText lines={9} />
            </TextContainer>
          </Card>
        </Layout.Section>
      </Layout>
    </SkeletonPage>
  );

  const pageMarkup = isLoading ? loadingPageMarkup : actualPageMarkup;

  const modalMarkup = (
    <Modal
      open={modalActive}
      onClose={toggleModalActive}
      title="Contact support"
      primaryAction={{
        content: "Send",
        onAction: toggleModalActive,
      }}
    >
      <Modal.Section>
        <FormLayout>
          <TextField
            label="Subject"
            value={supportSubject}
            onChange={handleSubjectChange}
          />
          <TextField
            label="Message"
            value={supportMessage}
            onChange={handleMessageChange}
            multiline
          />
        </FormLayout>
      </Modal.Section>
    </Modal>
  );

  const theme = {
    colors: {
      surface: "#FFFFFF",
      onsurface: "#212B36",
    },
  };
  const logo = {
    width: 150,
    topBarSource: logoImg,
    // url: "/admin/dashboard",
    // accessibilityLabel: "Jaded Pixel",
  };

  return (
    <div style={{ height: "500px" }}>
      <AppProvider
        // theme={theme}
        i18n={{
          Polaris: {
            Avatar: {
              label: "Avatar",
              labelWithInitials: "Avatar with initials {initials}",
            },
            ContextualSaveBar: {
              save: "Save",
              discard: "Discard",
            },
            TextField: {
              characterCount: "{count} characters",
            },
            TopBar: {
              toggleMenuLabel: "Toggle menu",

              SearchField: {
                clearButtonLabel: "Clear",
                search: "Search",
              },
            },
            Modal: {
              iFrameTitle: "body markup",
            },
            Frame: {
              skipToContent: "Skip to content",
              Navigation: {
                closeMobileNavigationLabel: "Close navigation",
              },
            },
          },
        }}
      >
        <Frame
          topBar={topBarMarkup}
          navigation={navigationMarkup}
          showMobileNavigation={mobileNavigationActive}
          onNavigationDismiss={toggleMobileNavigationActive}
          skipToContentTarget={skipToContentRef.current}
          logo={logo}
        >
          {props.children}
        </Frame>
      </AppProvider>
    </div>
  );
}
export default React.memo(NavigationLayout);
